import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Container, Grid, Box, Typography, IconButton, Link, useTheme } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import CONTACT_INFO from "../constants/contactInfo";
import NAV_ITEMS from "../constants/navigation";
import { sandPalette } from "../theme/colors";
import logo from "../assets/logo.png";

const SOCIALS = [
  { label: "Instagram", href: CONTACT_INFO.instagramLink, icon: InstagramIcon },
  { label: "Facebook", href: CONTACT_INFO.facebookLink, icon: FacebookIcon },
  { label: "WhatsApp", href: CONTACT_INFO.whatsappLink, icon: WhatsAppIcon },
];

const CONTACT = [
  { icon: LocationOnOutlinedIcon, text: CONTACT_INFO.address },
  {
    icon: PhoneOutlinedIcon,
    text: CONTACT_INFO.phone,
    href: `tel:${CONTACT_INFO.phone.replace(/\s/g, "")}`,
  },
  { icon: EmailOutlinedIcon, text: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
];

const muted = "text.secondary";

const ColumnTitle = ({ children }) => (
  <Typography variant="overline" component="h3" sx={{ color: "primary.main", mb: 2, display: "block" }}>
    {children}
  </Typography>
);

const Footer = () => {
  const theme = useTheme();

  return (
    <Box component="footer" sx={{ bgcolor: sandPalette[200], color: "text.primary", pt: { xs: 8, md: 10 } }}>
      <Container>
        <Grid container spacing={{ xs: 5, md: 6 }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                component="img"
                src={logo}
                alt=""
                sx={{ height: 56 }}
              />
              <Typography
                sx={{
                  fontFamily: theme.typography.h1.fontFamily,
                  fontSize: "1.4rem",
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Cabañas del Campo
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: muted, maxWidth: 360, mb: 3 }}>
              Disfrutá de la tranquilidad y la belleza natural de las sierras de
              Córdoba en nuestras cabañas.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              {SOCIALS.map(({ label, href, icon: Icon }) => (
                <IconButton
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  sx={{
                    color: "secondary.dark",
                    border: `1px solid ${sandPalette[400]}`,
                    transition: "background-color .25s ease, border-color .25s ease",
                    "&:hover": {
                      color: "white",
                      backgroundColor: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Icon fontSize="small" />
                </IconButton>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ColumnTitle>Explorá</ColumnTitle>
            <Box
              component="ul"
              sx={{
                listStyle: "none",
                p: 0,
                m: 0,
                display: "grid",
                gridTemplateColumns: "repeat(2, auto)",
                justifyContent: "start",
                columnGap: 4,
                rowGap: 1,
              }}
            >
              {NAV_ITEMS.map((item) => (
                <li key={item.path}>
                  <Link
                    component={RouterLink}
                    to={item.path}
                    underline="none"
                    variant="body2"
                    sx={{ color: muted, transition: "color .2s ease", "&:hover": { color: "primary.main" } }}
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <ColumnTitle>Contacto</ColumnTitle>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {CONTACT.map(({ icon: Icon, text, href }) => (
                <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Icon sx={{ color: "primary.main", fontSize: 20 }} />
                  {href ? (
                    <Link
                      href={href}
                      underline="none"
                      variant="body2"
                      sx={{ color: muted, "&:hover": { color: "primary.main" } }}
                    >
                      {text}
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ color: muted }}>
                      {text}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        <Box
          sx={{
            mt: { xs: 6, md: 8 },
            py: 3,
            borderTop: `1px solid ${sandPalette[300]}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            © {new Date().getFullYear()} Cabañas del Campo · Athos Pampa, Córdoba
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {CONTACT_INFO.instagram}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
